import { API_V1, fireLinkedinSessionInvalid, isLinkedinSessionInvalidApiError } from 'API/APIs/APIv1';
import config from 'config';
import { LinkedinTypes } from 'enums';
import type { TalentSearchFeedback } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { RecruiterConstraint } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import Logger from 'logger';

/** `leads` (Sales): title/text carry the product briefing and the AI searches its buyers. */
export type TalentSearchAudience = 'candidates' | 'leads';

export const searchLinkedinTalents = async (
  title: string,
  geoUrns: string[],
  product: string,
  text: string,
  campaignId: string | undefined,
  linkedinAccount: string | undefined,
  recruiterLocations?: { geoUrns?: RecruiterConstraint[]; postalCodes?: RecruiterConstraint[]; area?: string },
  languages?: string[],
  includeOwnContacts?: boolean,
  audience?: TalentSearchAudience
) => {
  try {
    const payload: any = {
      title,
      product,
      campaignId,
      linkedinAccount,
      text,
      languages,
      includeOwnContacts,
      audience,
    };

    if (product === LinkedinTypes.LINKEDIN) {
      payload.geoUrns = geoUrns;
    } else if (product === LinkedinTypes.SALES_NAV) {
      if (recruiterLocations && recruiterLocations.geoUrns && !_.isEmpty(recruiterLocations.geoUrns)) {
        payload.geoUrns = recruiterLocations.geoUrns;
      }

      if (recruiterLocations && recruiterLocations.postalCodes && !_.isEmpty(recruiterLocations.postalCodes)) {
        payload.postalCodes = recruiterLocations.postalCodes;
        payload.area = recruiterLocations.area;
      }
    } else if (product === LinkedinTypes.RECRUITER) {
      if (recruiterLocations && recruiterLocations.geoUrns && !_.isEmpty(recruiterLocations.geoUrns)) {
        payload.geoUrns = recruiterLocations.geoUrns;
      }

      if (recruiterLocations && recruiterLocations.postalCodes && !_.isEmpty(recruiterLocations.postalCodes)) {
        payload.postalCodes = recruiterLocations.postalCodes;
        payload.area = recruiterLocations.area;
      }
    }

    const response = await API_V1.post('/talent-search', payload);

    return response.data;
  } catch (e) {
    Logger.trace('Error while linkedin talent search!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export interface TalentSearchStreamEvent {
  type: string;
  message?: string;
  phase?: string;
  constraintName?: string;
  linkedinFreq?: number;
  completed?: number;
  total?: number;
  error?: string;
  talentSearches?: any[];
  feedback?: TalentSearchFeedback;
  autosave?: {
    savedSearches: number;
    duplicateSearches: number;
    saveError?: unknown;
  };
}

export const streamLinkedinTalents = async (
  payload: {
    title: string;
    geoUrns: string[];
    product: string;
    text: string;
    campaignId?: string;
    linkedinAccount: string | undefined;
    recruiterLocations?: { geoUrns?: RecruiterConstraint[]; postalCodes?: RecruiterConstraint[]; area?: string };
    languages?: string[];
    includeOwnContacts?: boolean;
    audience?: TalentSearchAudience;
  },
  onEvent: (event: TalentSearchStreamEvent) => void
) => {
  let streamSessionError: { code?: string; message?: string } | null = null;

  try {
    const streamPayload: any = {
      title: payload.title,
      product: payload.product,
      campaignId: payload.campaignId,
      linkedinAccount: payload.linkedinAccount,
      text: payload.text,
      languages: payload.languages,
      includeOwnContacts: payload.includeOwnContacts,
      audience: payload.audience,
    };

    if (payload.product === LinkedinTypes.LINKEDIN) {
      streamPayload.geoUrns = payload.geoUrns;
    } else if (payload.product === LinkedinTypes.SALES_NAV || payload.product === LinkedinTypes.RECRUITER) {
      if (payload.recruiterLocations?.geoUrns?.length) {
        streamPayload.geoUrns = payload.recruiterLocations.geoUrns;
      }
      if (payload.recruiterLocations?.postalCodes?.length) {
        streamPayload.postalCodes = payload.recruiterLocations.postalCodes;
        streamPayload.area = payload.recruiterLocations.area;
      }
    }

    const token = localStorage.getItem('user-token');
    const parsedToken = token ? JSON.parse(token) : null;
    const accessToken = parsedToken?.accessToken;

    const response = await fetch(`${config.apiBaseUrl}/talent-search/stream`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(streamPayload),
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => ({
        message: 'Failed to start talent search stream',
      }))) as Record<string, unknown>;
      if (isLinkedinSessionInvalidApiError(error) && payload.linkedinAccount) {
        fireLinkedinSessionInvalid(
          payload.linkedinAccount,
          {
            code: error.code != null ? String(error.code) : undefined,
            message: typeof error.message === 'string' ? error.message : undefined,
          },
          'talent-search-stream-http',
        );
      }
      return { error };
    }

    const reader = response.body?.getReader();
    if (!reader) {
      return { error: { message: 'No streaming body returned' } };
    }

    const decoder = new TextDecoder();
    let sseBuffer = '';
    const forwardEvent = (event: TalentSearchStreamEvent) => {
      if (event.type === 'error') {
        streamSessionError = {
          code: typeof event.error === 'string' ? event.error : undefined,
          message: event.message,
        };
      }
      onEvent(event);
    };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          forwardEvent(event);
        } catch {
          // ignore malformed/incomplete events
        }
      }
    }

    if (sseBuffer.startsWith('data: ')) {
      try {
        const event = JSON.parse(sseBuffer.slice(6));
        forwardEvent(event);
      } catch {
        // ignore trailing malformed event
      }
    }

    const sessionErrSnapshot = streamSessionError as { code?: string; message?: string } | null;
    if (
      sessionErrSnapshot &&
      payload.linkedinAccount &&
      isLinkedinSessionInvalidApiError(sessionErrSnapshot)
    ) {
      fireLinkedinSessionInvalid(
        payload.linkedinAccount,
        { code: sessionErrSnapshot.code, message: sessionErrSnapshot.message },
        'talent-search-stream',
      );
    }

    return { ok: true };
  } catch (e) {
    Logger.trace('Error while streaming linkedin talent search!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const searchLinkedinProspectsByEngagement = async (
  problems: string,
  features: string,
  benefits: string,
  impact: string,
  signal?: any
) => {
  try {
    const response = await API_V1.post(
      '/leadGen/contentSearch',
      {
        offer: { problems, features, benefits, impact },
      },
      {
        signal,
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while linkedin lead search via content search!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
